import { cancelAnimation, withSpring, withTiming } from "@-/com.npmjs.react-native-reanimated";
import { isSlideLearnCardPresentationKey } from "@-/dev.improve.haniverse.lib.learn.card-action";
import {
  LEARN_DECK_RECOVERY_SPRING,
  loadingCardParkTransform,
  upwardCardParkTransform,
} from "@-/dev.improve.haniverse.lib.learn.deck";
import type { LearnDeckStageValues } from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";

import {
  LEARN_DECK_EXIT_DURATION_MS,
  LEARN_DECK_HEADER_EXIT_CLEARANCE,
  LEARN_DECK_PARK_SPRING,
} from "#learn-deck-springs";

/** The way a card leaves the stage (an upward exit is a skip). */
export type LearnCardExitDirection = "left" | "right" | "up";

/** Where a card leaving in `direction` ends up: fully off the stage, clear of the toolbar. */
export function learnCardExitOffset(
  direction: LearnCardExitDirection,
  width: number,
  height: number,
): { x: number; y: number } {
  "worklet";
  switch (direction) {
    case "left": {
      return { x: -(width * 1.3), y: 0 };
    }
    case "right": {
      return { x: width * 1.3, y: 0 };
    }
    case "up": {
      // Clear the toolbar and the header fade above the stage, not just the card's own height.
      return { x: 0, y: -(height + LEARN_DECK_HEADER_EXIT_CLEARANCE) };
    }
  }
}

/** How far a slide card waits parked short of its successor, which shows that much at the edge. */
const PAGED_CARD_PARK_OFFSET = 9;

/**
 * How far a slide card pages on to its successor (`stage.pageStride`): at least the card's width
 * and the stage's 9pt gutter, which it is before the deck has measured.
 */
export function learnCardPageStride(stage: LearnDeckStageValues): number {
  "worklet";
  return Math.max(stage.pageStride.get(), stage.cardWidth.get() + 9);
}

/**
 * The successor's offset from rest while `presentationKey` is on show: a slide card pages on to it,
 * so it waits one page stride to the right; any other card covers it (`null`).
 */
export function pagedSuccessorOffset(
  stage: LearnDeckStageValues,
  presentationKey: string,
): number | null {
  "worklet";
  return isSlideLearnCardPresentationKey(presentationKey) ? learnCardPageStride(stage) : null;
}

/**
 * Where `presentationKey` ends up leaving in `direction`. A slide card is never swiped off: however
 * it leaves, it pages on, untilted, one page stride to the left, as its successor pages in
 * (`pageInLearnCardSuccessor`).
 */
export function learnCardExitTarget(
  stage: LearnDeckStageValues,
  presentationKey: string,
  direction: LearnCardExitDirection,
): { x: number; y: number } {
  "worklet";
  if (isSlideLearnCardPresentationKey(presentationKey)) {
    return { x: -learnCardPageStride(stage), y: 0 };
  }
  return learnCardExitOffset(
    direction,
    Math.max(stage.cardWidth.get(), 320),
    Math.max(stage.cardHeight.get(), 480),
  );
}

/**
 * Where `presentationKey` waits when its action is committed but it cannot leave yet: at the edge,
 * leaning toward it; square, just above rest, for an upward exit; and for a slide card, untilted
 * and turned a little toward its successor, whose edge shows at the screen's.
 */
export function learnCardParkPose(
  stage: LearnDeckStageValues,
  presentationKey: string,
  direction: LearnCardExitDirection,
): { tilt: number; x: number; y: number } {
  "worklet";
  if (isSlideLearnCardPresentationKey(presentationKey)) {
    return { tilt: 0, x: -PAGED_CARD_PARK_OFFSET, y: 0 };
  }
  return direction === "up"
    ? upwardCardParkTransform()
    : loadingCardParkTransform(direction, stage.cardWidth.get(), stage.cardHeight.get());
}

/**
 * A slide card left from `fromX`: its successor, which waited beside it, has just taken the shared
 * motion at rest. Put it back beside the card and page it in on the departure's clock, so the two
 * travel as one page. Any other card's successor was underneath it and stays at rest.
 */
export function pageInLearnCardSuccessor(
  stage: LearnDeckStageValues,
  departingKey: string,
  fromX: number,
): void {
  "worklet";
  const offset = pagedSuccessorOffset(stage, departingKey);
  if (offset === null) {
    return;
  }
  stage.dragX.set(fromX + offset);
  stage.dragX.set(withTiming(0, { duration: LEARN_DECK_EXIT_DURATION_MS }));
}

/** The shared motion at rest for the card that now owns it, with no drag or flight under way. */
function restMotion(stage: LearnDeckStageValues): void {
  "worklet";
  stage.gestureId.set(stage.gestureId.get() + 1);
  stage.isDragging.set(false);
  stage.exitingOperation.set(null);
  stage.dragX.set(0);
  stage.dragY.set(0);
  stage.tilt.set(0);
  stage.exitProgress.set(0);
  stage.flipProgress.set(0);
}

/**
 * Show `successorKey`, the deck's ready next flashcard, in place of the released `presentationKey`:
 * it takes the shared motion at rest and the next touch.
 */
function showReleasedSuccessor(
  stage: LearnDeckStageValues,
  presentationKey: string,
  successorKey: string,
): void {
  "worklet";
  stage.release.set({ from: presentationKey, to: successorKey });
  stage.motionPresentation.set({ current: successorKey, dismissed: presentationKey });
  restMotion(stage);
}

/**
 * Send `presentationKey` off the stage the moment it is released, from wherever the finger left
 * it, uncovering the next flashcard, which takes the next touch in the same frame. Nothing here
 * waits for React: the action's write still runs, and the exit adopts this motion when it lands
 * (`completeLearnCardRelease`); a failure brings the card back (`returnReleasedLearnCard`).
 *
 * A card is only released onto a next flashcard that is ready to be shown. Until it is, the card
 * STALLS at the snap point (`stage.stalledRelease`): it settles at its parked edge (square, just
 * above rest, for an upward exit), and `resumeStalledLearnCardRelease` flies it off the moment
 * that flashcard has rendered its front. A re-grab while it waits returns it to the snap point.
 */
export function releaseLearnCard(
  stage: LearnDeckStageValues,
  presentationKey: string,
  direction: LearnCardExitDirection,
): void {
  "worklet";
  const successorKey = stage.successorPresentation.get();
  stage.committedPresentation.set(presentationKey);
  if (successorKey === null || successorKey === presentationKey || !stage.isSuccessorReady.get()) {
    stage.stalledRelease.set({ direction, presentation: presentationKey });
    const parked = learnCardParkPose(stage, presentationKey, direction);
    stage.dragX.set(withSpring(parked.x, LEARN_DECK_PARK_SPRING));
    stage.dragY.set(withSpring(parked.y, LEARN_DECK_PARK_SPRING));
    stage.tilt.set(withSpring(parked.tilt, LEARN_DECK_PARK_SPRING));
    return;
  }
  const target = learnCardExitTarget(stage, presentationKey, direction);
  cancelAnimation(stage.dragX);
  cancelAnimation(stage.dragY);
  cancelAnimation(stage.tilt);
  stage.stalledRelease.set(null);
  const fromX = stage.dragX.get();
  stage.departure.set({
    presentation: presentationKey,
    tilt: stage.tilt.get(),
    toX: target.x,
    toY: target.y,
    x: fromX,
    y: stage.dragY.get(),
  });
  stage.departureProgress.set(0);
  stage.departureProgress.set(withTiming(1, { duration: LEARN_DECK_EXIT_DURATION_MS }));
  showReleasedSuccessor(stage, presentationKey, successorKey);
  pageInLearnCardSuccessor(stage, presentationKey, fromX);
}

/**
 * Fly off the card stalled at the snap point once the next flashcard is ready, unless a finger is
 * on it, its exit has already been claimed (which then flies it itself), or another card has taken
 * the motion. Run from a UI-thread reaction on the successor's readiness.
 */
export function resumeStalledLearnCardRelease(stage: LearnDeckStageValues): void {
  "worklet";
  const stalled = stage.stalledRelease.get();
  if (
    stalled === null ||
    !stage.isSuccessorReady.get() ||
    stage.isDragging.get() ||
    stage.exitingOperation.get() !== null ||
    stage.release.get() !== null ||
    stage.motionPresentation.get().current !== stalled.presentation
  ) {
    return;
  }
  releaseLearnCard(stage, stalled.presentation, stalled.direction);
}

/**
 * The released card's exit started: hand its successor the motion for good. The successor already
 * on show keeps whatever it is doing, including a drag under way, and the departure keeps flying.
 * Returns false when `presentationKey` was not released (it stalled, or parked), so the caller runs
 * the ordinary exit instead.
 */
export function completeLearnCardRelease(
  stage: LearnDeckStageValues,
  presentationKey: string,
  successorKey: string,
  epoch: number,
): boolean {
  "worklet";
  const release = stage.release.get();
  if (release === null || release.from !== presentationKey) {
    return false;
  }
  stage.release.set(null);
  if (stage.committedPresentation.get() === presentationKey) {
    stage.committedPresentation.set(null);
  }
  stage.motionEpoch.set(epoch);
  if (stage.motionPresentation.get().current === successorKey) {
    stage.exitingOperation.set(null);
    return true;
  }
  stage.motionPresentation.set({ current: successorKey, dismissed: presentationKey });
  restMotion(stage);
  return true;
}

/**
 * The released card's action did not go through (the write failed or was refused, or the next
 * card could not be loaded): bring it back from wherever its flight has reached, on the face it
 * left on. Whatever was shown in its place goes back underneath, and a drag on it ends. A card
 * stalled at the snap point springs home from there. Returns false when `presentationKey` was
 * neither released nor stalled.
 */
export function returnReleasedLearnCard(
  stage: LearnDeckStageValues,
  presentationKey: string,
  face: "back" | "front",
): boolean {
  "worklet";
  if (stage.stalledRelease.get()?.presentation === presentationKey) {
    stage.stalledRelease.set(null);
    if (stage.committedPresentation.get() === presentationKey) {
      stage.committedPresentation.set(null);
    }
    if (stage.isDragging.get() || stage.exitingOperation.get() !== null) {
      return true;
    }
    stage.dragX.set(withSpring(0, LEARN_DECK_RECOVERY_SPRING));
    stage.dragY.set(withSpring(0, LEARN_DECK_RECOVERY_SPRING));
    stage.tilt.set(withSpring(0, LEARN_DECK_RECOVERY_SPRING));
    return true;
  }
  const release = stage.release.get();
  if (release === null || release.from !== presentationKey) {
    return false;
  }
  const departure = stage.departure.get();
  const progress = stage.departureProgress.get();
  cancelAnimation(stage.departureProgress);
  const flown =
    departure?.presentation === presentationKey
      ? {
          tilt: departure.tilt,
          x: departure.x + (departure.toX - departure.x) * progress,
          y: departure.y + (departure.toY - departure.y) * progress,
        }
      : { tilt: 0, x: 0, y: 0 };
  stage.release.set(null);
  stage.committedPresentation.set(null);
  stage.departure.set(null);
  stage.departureProgress.set(0);
  stage.motionPresentation.set({ current: presentationKey, dismissed: release.to });
  restMotion(stage);
  stage.flipProgress.set(face === "back" ? 1 : 0);
  stage.dragX.set(flown.x);
  stage.dragY.set(flown.y);
  stage.tilt.set(flown.tilt);
  stage.dragX.set(withSpring(0, LEARN_DECK_RECOVERY_SPRING));
  stage.dragY.set(withSpring(0, LEARN_DECK_RECOVERY_SPRING));
  stage.tilt.set(withSpring(0, LEARN_DECK_RECOVERY_SPRING));
  return true;
}
